import type {
  DomainRouterConnection,
  DomainRouterJsonInput,
} from '@triedge/domain-core';
import type {
  Employee,
  ExportPayrollPeriodResult,
  PayPeriod,
  PayrollAuditLog,
  PayrollDashboard,
  PayrollEmployeeDetail,
  Position,
  StipendAssignment,
  TimeEntry,
  Adjustment,
  ExternalTuitionPayment,
  DeductionEntry,
} from './entities';

export const PAYROLL_QUERY_OPERATION_IDS = [
  'getPayrollDashboard',
  'getPayrollEmployeeDetail',
  'listPayrollAuditLog',
] as const;

export const PAYROLL_MUTATION_OPERATION_IDS = [
  'switchPayrollPeriod',
  'upsertPayrollEmployeeProfile',
  'upsertPayrollPosition',
  'upsertPayrollStipend',
  'upsertPayrollTimeEntry',
  'upsertPayrollAdjustment',
  'upsertPayrollExternalTuitionPayment',
  'upsertPayrollDeductionEntry',
  'submitPayrollPeriodForApproval',
  'approvePayrollPeriod',
  'rejectPayrollPeriod',
  'unlockPayrollPeriod',
  'exportPayrollPeriod',
] as const;

export type PayrollQueryOperationId = (typeof PAYROLL_QUERY_OPERATION_IDS)[number];
export type PayrollMutationOperationId = (typeof PAYROLL_MUTATION_OPERATION_IDS)[number];
export type PayrollAuditLogRouterConnection = DomainRouterConnection<PayrollAuditLog>;

export type PayrollQueryArgsMap = {
  getPayrollDashboard: { tenantId?: string; periodId?: string };
  getPayrollEmployeeDetail: { tenantId?: string; periodId?: string; employeeId: string };
  listPayrollAuditLog: { tenantId?: string; employeeId?: string; periodId?: string; limit?: number };
};

export type PayrollQueryResultMap = {
  getPayrollDashboard: PayrollDashboard;
  getPayrollEmployeeDetail: PayrollEmployeeDetail;
  listPayrollAuditLog: PayrollAuditLogRouterConnection;
};

export type PayrollMutationArgsMap = {
  switchPayrollPeriod: { input: { tenantId?: string; periodId: string } | DomainRouterJsonInput };
  upsertPayrollEmployeeProfile: { input: { tenantId?: string; periodId?: string; employeeId: string; profile: Partial<Pick<Employee, 'email' | 'status' | 'leaveInfo'>> } | DomainRouterJsonInput };
  upsertPayrollPosition: { input: { tenantId?: string; periodId?: string; employeeId: string; position: Partial<Position> } | DomainRouterJsonInput };
  upsertPayrollStipend: { input: { tenantId?: string; periodId?: string; employeeId: string; stipend: Partial<StipendAssignment> } | DomainRouterJsonInput };
  upsertPayrollTimeEntry: { input: { tenantId?: string; periodId?: string; employeeId: string; timeEntry: Partial<TimeEntry> } | DomainRouterJsonInput };
  upsertPayrollAdjustment: { input: { tenantId?: string; periodId?: string; employeeId: string; adjustment: Partial<Adjustment> } | DomainRouterJsonInput };
  upsertPayrollExternalTuitionPayment: { input: { tenantId?: string; periodId?: string; employeeId: string; externalTuitionPayment: Partial<ExternalTuitionPayment> } | DomainRouterJsonInput };
  upsertPayrollDeductionEntry: { input: { tenantId?: string; periodId?: string; employeeId: string; deductionEntry: Partial<DeductionEntry> } | DomainRouterJsonInput };
  submitPayrollPeriodForApproval: { input: { tenantId?: string; periodId: string; notes?: string } | DomainRouterJsonInput };
  approvePayrollPeriod: { input: { tenantId?: string; periodId: string; notes?: string } | DomainRouterJsonInput };
  rejectPayrollPeriod: { input: { tenantId?: string; periodId: string; reason: string } | DomainRouterJsonInput };
  unlockPayrollPeriod: { input: { tenantId?: string; periodId: string; reason?: string } | DomainRouterJsonInput };
  exportPayrollPeriod: { input: { tenantId?: string; periodId: string; providerTemplateId: string; exportFormat?: string } | DomainRouterJsonInput };
};

export type PayrollMutationResultMap = {
  switchPayrollPeriod: PayrollDashboard;
  upsertPayrollEmployeeProfile: PayrollEmployeeDetail;
  upsertPayrollPosition: PayrollEmployeeDetail;
  upsertPayrollStipend: PayrollEmployeeDetail;
  upsertPayrollTimeEntry: PayrollEmployeeDetail;
  upsertPayrollAdjustment: PayrollEmployeeDetail;
  upsertPayrollExternalTuitionPayment: PayrollEmployeeDetail;
  upsertPayrollDeductionEntry: PayrollEmployeeDetail;
  submitPayrollPeriodForApproval: PayrollDashboard;
  approvePayrollPeriod: PayrollDashboard;
  rejectPayrollPeriod: PayrollDashboard;
  unlockPayrollPeriod: PayrollDashboard;
  exportPayrollPeriod: ExportPayrollPeriodResult;
};

export type PayrollMutationEntityResultMap = {
  payPeriod: PayPeriod;
};
